import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/styles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        'group/input-group border-input dark:bg-input/30 rounded-50 relative flex w-full items-center border border-transparent bg-white shadow-xs transition-[color,box-shadow] outline-none',
        'h-44 min-w-0 has-[>textarea]:h-auto sm:h-46',

        // Focus state.
        'has-[[data-slot=input-group-control]:focus-visible]:border-[#409EFF] has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot=input-group-control]:focus-visible]:ring-[#409EFF]/50',

        // Error state.
        'has-[[data-slot][aria-invalid=true]]:border-[#FF4343] has-[[data-slot][aria-invalid=true]]:ring-[#FF4343]/20 dark:has-[[data-slot][aria-invalid=true]]:ring-[#FF4343]/40',

        className,
      )}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva('flex h-auto cursor-text items-center justify-center font-medium select-none group-data-[disabled=true]/input-group:opacity-50', {
  variants: {
    align: {
      'inline-start': 'order-first pl-12 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
      'inline-end': 'order-last pr-12 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]',
      'block-start': 'order-first w-full justify-start px-12 pt-12 [.border-b]:pb-12 group-has-[>input]/input-group:pt-10',
      'block-end': 'order-last w-full justify-start px-12 pb-12 [.border-t]:pt-12 group-has-[>input]/input-group:pb-10',
    },
  },
  defaultVariants: {
    align: 'inline-start',
  },
})

function InputGroupAddon({ className, align = 'inline-start', ...props }: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva('text-12 shadow-none flex gap-8 items-center', {
  variants: {
    size: {
      '24': 'h-96 px-10 gap-6 rounded-md has-[>svg]:px-10',
    },
  },
  defaultVariants: {
    size: '24',
  },
})

function InputGroupButton({
  className,
  type = 'button',
  variant = 'default',
  size = '24',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> & VariantProps<typeof inputGroupButtonVariants>) {
  return <Button type={type} data-size={size} variant={variant} className={cn(inputGroupButtonVariants({ size }), className)} {...props} />
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return <span className={cn('text-16 flex items-center gap-8 text-[#6d7693] [&_svg]:pointer-events-none', className)} {...props} />
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return <Input data-slot="input-group-control" className={cn('flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent', className)} {...props} />
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn('flex-1 resize-none rounded-none border-0 bg-transparent py-12 shadow-none focus-visible:ring-0 dark:bg-transparent', className)}
      {...props}
    />
  )
}

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupInput, InputGroupTextarea }
